import type { ShareableType } from '../resource';
import type { ShareableLabel } from './shareable-label.model';

export interface ShareTarget {
  id: string;
  type: ShareableType;
}

export interface ShareableObj extends ShareTarget {
  name: string;
  createdDate: Date;
  modifiedDate: Date;
  parentFolderId: string;
  allowedShareablePermissions: BdSharePermission[];
  labels?: ShareableLabel[];
}

export interface ShareableFolderChild {
  parentFolderId?: string;
  shareableId: string;
  shareableType: ShareableType;
  isFolder: boolean;
  name: string;
  modifiedDate: string;
  allowedShareablePermissions: BdSharePermission[];
  labels?: ShareableLabel[];
}

/**
 * Earliest representable `Date` as an ISO string. Used as the default
 * `modifiedDate` for folder children that the API returns without one, so
 * they sort to the bottom of date-sorted lists and never break the
 * sorting comparators or date templates that require a parseable value.
 */
export const MIN_MODIFIED_DATE = new Date(-8640000000000000).toISOString();

/** Marks the kind of a folder; `ROOT` is the (home-icon) top of a scope tree. */
export type ShareableFolderType = 'ROOT' | 'REGULAR';

/**
 * A single node in a folder's ancestor chain, as returned nested under
 * `ShareableFolderObj.parent` (BDA-5484). The chain walks upward — each node's
 * `parent` is the next ancestor — terminating at the `ROOT` node whose
 * `parent` is `null`. Used to reconstruct the breadcrumb for a deep-linked
 * folder (BDA-5535).
 */
export interface ShareableFolderAncestor {
  folderId: string;
  name: string;
  folderType: ShareableFolderType;
  parent: ShareableFolderAncestor | null;
}

export interface ShareableFolderObj {
  folderId: string;
  name: string;
  children: ShareableFolderChild[];
  parentFolderId?: string;
  folderType?: ShareableFolderType;
  /**
   * Ancestor chain from this folder up to (and including) the scope root.
   * `null` for a root folder, `undefined` when the backend omits it. See
   * `ShareableFolderAncestor`.
   */
  parent?: ShareableFolderAncestor | null;
}

export type ShareTargetType = 'user' | 'group' | 'company';

export type GroupMemberType = 'ADMIN' | 'CONTRIBUTOR' | 'READER';

export interface ShareableReference {
  id: string;
  type: ShareTargetType;
  /**
   * Human-readable label for the share target, when the backend resolves it.
   * The permission endpoints (e.g. the access graph) return this for every
   * target — including group grants, whose name is a descriptive share-group
   * label rather than a company/group pairing. Prefer it over any client-side
   * id resolution; it may be absent/null on endpoints that don't populate it
   * (e.g. permissions/current).
   */
  name?: string | null;
}

export interface FeaturePermissionObj {
  permission: `${string}:${string}`;
  granteeId: string;
  granteeType: ShareTargetType;
  grantorId: string;
  grantorType: ShareTargetType;
  active: boolean;
  createdDate: Date;
  expiresAt: Date | null;
}

/**
 * Permission types are ordered ascending.
 */
export type BdSharePermission = 'READ' | 'UPDATE' | 'DELETE';

/**
 * Short German nouns for a permission, used where the long
 * {@link BD_SHARE_PERMISSION_DISPLAY_NAME} phrasing ("Kann anzeigen") would not
 * fit — notably inside the access-graph path sentences, which name a permission
 * mid-clause.
 */
export const BD_SHARE_PERMISSION_SHORT_NAME: {
  [permission in BdSharePermission]: string;
} = {
  READ: 'Anzeigen',
  UPDATE: 'Bearbeiten',
  DELETE: 'Löschen',
};

export const BD_SHARE_PERMISSION_DISPLAY_NAME: {
  [locale: string]: {
    [permission in BdSharePermission]: string;
  };
} = {
  de: {
    READ: 'Kann anzeigen',
    UPDATE: 'Kann bearbeiten',
    DELETE: 'Kann bearbeiten und löschen',
  },
  'de-DE': {
    READ: 'Kann anzeigen',
    UPDATE: 'Kann bearbeiten',
    DELETE: 'Kann bearbeiten und löschen',
  },
  en: {
    READ: 'Able to view',
    UPDATE: 'Able to edit',
    DELETE: 'Able to edit and delete',
  },
};
